// src/components/admin/employees/EmployeeDetails.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Edit, Save, X, Award, MessageSquare, MoreVertical,
    FileText, UserX, Lock, Smartphone, KeyRound, CreditCard, LogOut, Ban
} from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/useAuth';
import EmployeeProfileView from '../../common/EmployeeProfileView';
import EmployeeActionPanels from './EmployeeActionPanels';
import { isInExitProcess, isExitDatePassed, hasExited } from '../../../utils/exitStatusHelper';
import exitService from '../../../services/exit.service';
import { toISTDateString } from '../../../utils/dateHelpers';

// Helper to format date for input[type="date"] - uses IST timezone
const formatDateForInputIST = (dateStr) => {
    if (!dateStr) return '';
    const istDate = toISTDateString(dateStr);
    return istDate && istDate !== 'Invalid Date' ? istDate : '';
};

const EmployeeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { user } = useAuth();
    const { employees, updateEmployee, departments, getPotentialManagers, fetchEmployees, loading: employeesLoading } = useEmployees();
    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const [activeSidePanel, setActiveSidePanel] = useState(null);
    const [exitRequest, setExitRequest] = useState(null);
    const [completingExit, setCompletingExit] = useState(false);
    const moreOptionsRef = useRef(null);

    // Find employee from EmployeeContext
    const employee = employees.find(emp => emp.id === id);

    // Check if viewing own profile
    const isViewingOwnProfile = user?.id === id || user?.email === employee?.email;

    useEffect(() => {
        if (!employee) {
            setLoading(false);
        } else if (!isEditing) {
            // Only initialize form data when not in edit mode to prevent overwriting user changes
            console.log("check the employee data"+JSON.stringify(employee));
            // Initialize edit form data with current employee data
            setEditFormData({
                // Basic details (About tab - Summary)
                first_name: employee.first_name || '',
                middle_name: employee.middle_name || '',
                last_name: employee.last_name || '',
                display_name: employee.display_name || '',
                personal_email: employee.personal_email || '',
                phone: employee.phone || '',
                date_of_birth: formatDateForInputIST(employee.date_of_birth),
                gender: employee.gender || '',
                nationality: employee.nationality || employee.country || '',
                marital_status: employee.marital_status || '',

                // Address (About tab - Summary)
                address: employee.address || '',
                city: employee.city || '',
                state: employee.state || '',
                country: employee.country || '',
                postal_code: employee.postal_code || '',

                // Emergency Contact (About tab - Summary)
                emergency_contact_name: employee.emergency_contact_name || '',
                emergency_contact_relation: employee.emergency_contact_relation || '',
                emergency_contact_phone: employee.emergency_contact_phone || '',
                emergency_contact_email: employee.emergency_contact_email || '',
                emergency_contact_address: employee.emergency_contact_address || '',

                // Job details (Job tab)
                email: employee.email || '',
                employee_code: employee.employee_code || '',
                department_id: employee.department_id || '',
                designation: employee.designation || '',
                joining_date: formatDateForInputIST(employee.joining_date || employee.joiningDate),
                employment_type: employee.employment_type || 'full-time',
                employment_status: employee.employment_status || 'active',
                managerId: employee.managerId || employee.reporting_manager_id || '',
                work_location: employee.work_location || '',
                legal_entity: employee.legal_entity || employee.legalEntity || '',
                work_country: employee.work_country || employee.workCountry || '',
                probation_policy: employee.probation_policy || employee.probationPolicy || '',
                notice_period: employee.notice_period || employee.noticePeriod || '',

                // Work & Attendance Details
                leave_plan: employee.leave_plan || employee.leavePlan || '',
                holiday_plan_id: employee.holiday_plan_id || employee.holiday_list || employee.holidayList || '',
                shift_id: employee.shift_id || '',
                weekly_off: employee.weekly_off || employee.weeklyOff || '',
                attendance_capture_scheme: employee.attendance_capture_scheme || employee.attendanceCaptureScheme || '',
                attendance_number: employee.attendance_number || employee.attendanceNumber || '',

                // Compensation Details
                ctc: employee.pay_package?.ctc || employee.ctc || '',
                currency: employee.pay_package?.currency || employee.currency || 'INR',
                payroll_frequency: employee.pay_package?.payroll_frequency || employee.payroll_frequency || 'Monthly',
                effective_date: formatDateForInputIST(employee.pay_package?.effective_date || employee.effective_date || ''),

                // Bank Details
                bank_name: employee.bank_name || employee.pay_package?.compensation?.bank?.bankName || '',
                account_number: employee.account_number || employee.pay_package?.compensation?.bank?.accountNumber || '',
                ifsc_code: employee.ifsc_code || employee.pay_package?.compensation?.bank?.ifscCode || '',
                branch_name: employee.branch_name || employee.pay_package?.compensation?.bank?.branchName || ''
            });
        }
    }, [employee, isEditing]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moreOptionsRef.current && !moreOptionsRef.current.contains(event.target)) {
                setShowMoreOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch the employee's exit request unconditionally — the employees row is
    // often stale (exit_initiated_at / exit_completed_at not set) even when an
    // exit_request exists with a real exit date, so we can't gate on isInExitProcess.
    useEffect(() => {
        const fetchExitRequest = async () => {
            if (!employee?.id) return;
            try {
                const response = await exitService.getExitByEmployee(employee.id);
                if (response.success && response.data) {
                    setExitRequest(response.data);
                } else {
                    setExitRequest(null);
                }
            } catch (error) {
                // 404 just means no exit on file — not an error worth surfacing.
                if (error?.response?.status !== 404) {
                    console.error('Error fetching exit request:', error);
                }
                setExitRequest(null);
            }
        };
        fetchExitRequest();
    }, [employee]);

    // Check if exit date has passed
    const exitDateStatus = isExitDatePassed(employee, exitRequest);

    // Compute "this employee has exited" banner data — checks every signal:
    //   1. employee.exit_completed_at (the canonical "exit done" flag)
    //   2. employee.employment_status === 'exited'
    //   3. exit_request in a post-approval state with a real exit date
    const exitedDisplay = (() => {
        const isCompletedExit = hasExited(employee)
            || ['completed', 'clearance_complete', 'settlement_pending', 'in_clearance']
                .includes(exitRequest?.status);
        if (!isCompletedExit) return null;
        const dateStr = employee?.exit_completed_at
            || exitRequest?.actual_exit_date
            || exitRequest?.approved_exit_date
            || exitRequest?.last_working_day
            || exitRequest?.requested_exit_date;
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return {
            date,
            label: date.toLocaleDateString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata',
            }),
            reason: exitRequest?.exit_reason || exitRequest?.exit_type || null,
        };
    })();

    // Handle completing the exit
    const handleCompleteExit = async () => {
        if (!exitRequest?.id) {
            alert('No active exit request found');
            return;
        }

        if (!window.confirm(`Are you sure you want to complete the exit process for ${employee.first_name} ${employee.last_name}?\n\nThis will:\n- Mark employee status as "Exited"\n- Disable their login access\n- Record exit completion date`)) {
            return;
        }

        setCompletingExit(true);
        try {
            const response = await exitService.completeExit(exitRequest.id);
            if (response.success) {
                alert('Exit process completed successfully!');
                // Refresh employee data
                await fetchEmployees(true);
            } else {
                alert(response.message || 'Failed to complete exit process');
            }
        } catch (error) {
            console.error('Error completing exit:', error);
            alert('An error occurred while completing the exit process');
        } finally {
            setCompletingExit(false);
        }
    };

    const potentialManagers = getPotentialManagers().filter(manager => manager.id !== id);

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        // Preserve empty strings to allow users to clear fields
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveEdit = async () => {
        try {
            //console.log('💾 Saving employee data...');
            //console.log('Fields being saved:', Object.keys(editFormData));
            //console.log('🔍 leave_plan value being sent:', editFormData.leave_plan);
            //console.log('Full data:', editFormData);

            // Validate Date of Birth is provided
            if (!editFormData.date_of_birth || editFormData.date_of_birth.trim() === '') {
                alert('Date of Birth is mandatory. Please provide a valid date of birth before saving.');
                return;
            }

            await updateEmployee(id, editFormData);

            //console.log('✅ Employee updated successfully');
            //console.log('🔍 Checking employee object after update:', employee);
            //console.log('🔍 leave_plan in employee object:', employee?.leave_plan);
            setIsEditing(false);
            alert('Employee updated successfully!');
        } catch (error) {
            console.error('❌ Error updating employee:', error);
            console.error('Error response:', error.response?.data);

            // Show detailed error message
            const errorMessage = error.response?.data?.details
                || error.response?.data?.error
                || error.message
                || 'Failed to update employee';
            const errorCode = error.response?.data?.code;

            const dbDetail = error.response?.data?.dbDetail;

            let fullError = `Error: ${errorMessage}`;
            if (errorCode) fullError += `\nCode: ${errorCode}`;
            if (dbDetail) fullError += `\nDetail: ${dbDetail}`;

            alert(fullError);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        // Reset form data to original employee data
        setEditFormData({
            // Basic details (About tab - Summary)
            first_name: employee.first_name || '',
            middle_name: employee.middle_name || '',
            last_name: employee.last_name || '',
            display_name: employee.display_name || '',
            personal_email: employee.personal_email || '',
            phone: employee.phone || '',
            date_of_birth: formatDateForInputIST(employee.date_of_birth),
            gender: employee.gender || '',
            nationality: employee.nationality || employee.country || '',
            marital_status: employee.marital_status || '',

            // Address (About tab - Summary)
            address: employee.address || '',
            city: employee.city || '',
            state: employee.state || '',
            country: employee.country || '',
            postal_code: employee.postal_code || '',

            // Emergency Contact (About tab - Summary)
            emergency_contact_name: employee.emergency_contact_name || '',
            emergency_contact_relation: employee.emergency_contact_relation || '',
            emergency_contact_phone: employee.emergency_contact_phone || '',
            emergency_contact_email: employee.emergency_contact_email || '',
            emergency_contact_address: employee.emergency_contact_address || '',

            // Job details (Job tab)
            email: employee.email || '',
            employee_code: employee.employee_code || '',
            department_id: employee.department_id || '',
            designation: employee.designation || '',
            joining_date: formatDateForInputIST(employee.joining_date || employee.joiningDate),
            employment_type: employee.employment_type || 'full-time',
            employment_status: employee.employment_status || 'active',
            managerId: employee.managerId || employee.reporting_manager_id || '',
            work_location: employee.work_location || '',
            legal_entity: employee.legal_entity || employee.legalEntity || '',
                            work_country: employee.work_country || employee.workCountry || '',
            probation_policy: employee.probation_policy || employee.probationPolicy || '',            notice_period: employee.notice_period || employee.noticePeriod || '',

            // Work & Attendance Details
            leave_plan: employee.leave_plan || employee.leavePlan || '',
            holiday_plan_id: employee.holiday_plan_id || employee.holiday_list || employee.holidayList || '',
            shift_id: employee.shift_id || '',
            weekly_off: employee.weekly_off || employee.weeklyOff || '',
            attendance_capture_scheme: employee.attendance_capture_scheme || employee.attendanceCaptureScheme || '',
            attendance_number: employee.attendance_number || employee.attendanceNumber || ''
        });
    };

    // Show loading while employees are being fetched from context
    if (employeesLoading || loading) {
        return (
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading employee profile...</p>
            </div>
        );
    }

    // Only show "not found" if loading is complete and employee doesn't exist
    if (!employee) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/employees')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Employee Details</h1>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-6 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Employee not found</p>
                </div>
            </div>
        );
    }

    const handlePhotoUpload = (e) => {
        // TODO: Implement photo upload
        //console.log('Photo upload not implemented');
    };

    const handleMoreOptionClick = (action) => {
        setShowMoreOptions(false);
        setActiveSidePanel(action);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/employees')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                    </button>
                    <h1 className="tour-employee-profile-header text-2xl font-bold text-gray-900 dark:text-white">Employee Profile</h1>
                </div>

                <div className="flex items-center gap-3">
                    {/* Action Icons */}
                    {!isEditing && (
                        <>
                            {/* Only show Give Praise and Give Feedback buttons when viewing other employees, not self */}
                            {!isViewingOwnProfile && (
                                <>
                                    {/* Give Praise Icon */}
                                    <button
                                        onClick={() => setActiveSidePanel('praise')}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600"
                                        title="Give Praise"
                                    >
                                        <Award className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    </button>

                                    {/* Give Feedback Icon */}
                                    <button
                                        onClick={() => setActiveSidePanel('feedback')}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600"
                                        title="Give Feedback"
                                    >
                                        <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    </button>
                                </>
                            )}

                            {/* More Options Icon */}
                            <div className="relative" ref={moreOptionsRef}>
                                <button
                                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                                    className="tour-employee-more-options p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600"
                                    title="More Options"
                                >
                                    <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                </button>

                                {/* Dropdown Menu */}
                                {showMoreOptions && (
                                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-2 z-50">
                                        {/* TODO: Implement these features later
                                        <button
                                            onClick={() => handleMoreOptionClick('idcard')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">ID Card</span>
                                        </button>
                                        <button
                                            onClick={() => handleMoreOptionClick('internal-note')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">Write Internal Note</span>
                                        </button>
                                        <button
                                            onClick={() => handleMoreOptionClick('request-feedback')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">Request Feedback</span>
                                        </button>
                                        */}
                                        <button
                                            onClick={() => handleMoreOptionClick(isInExitProcess(employee) ? 'edit-exit' : 'initiate-exit')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <LogOut className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">
                                                {isInExitProcess(employee) ? 'Edit Exit' : 'Initiate Exit'}
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => handleMoreOptionClick('withhold-salary')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <Ban className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">
                                                Withhold Salary
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => handleMoreOptionClick(employee?.login_disabled ? 'enable-login' : 'disable-login')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <UserX className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">
                                                {employee?.login_disabled ? 'Enable Login' : 'Disable Login'}
                                            </span>
                                        </button>
                                        {/* TODO: Implement mobile login toggle later
                                        <button
                                            onClick={() => handleMoreOptionClick('disable-mobile-login')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <Smartphone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">Disable Mobile App Login</span>
                                        </button>
                                        */}
                                        <button
                                            onClick={() => handleMoreOptionClick('reset-password')}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                        >
                                            <KeyRound className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                            <span className="text-sm text-gray-900 dark:text-white">Reset Password</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {isEditing ? (
                        <div className="flex gap-2">
                            <button
                                onClick={handleSaveEdit}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                            >
                                <Save className="w-4 h-4" />
                                Save Changes
                            </button>
                            <button
                                onClick={handleCancelEdit}
                                className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                                    theme === 'dark'
                                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <X className="w-4 h-4" />
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="tour-employee-edit-button px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                            <Edit className="w-4 h-4" />
                            Edit Employee
                        </button>
                    )}
                </div>
            </div>

            {/* Already-Exited Banner — shown when the employee has fully exited */}
            {exitedDisplay && (
                <div className="bg-gray-100 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <LogOut className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Employee Exited
                            </h3>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                                {employee?.first_name} {employee?.last_name} exited on{' '}
                                <strong>{exitedDisplay.label}</strong>
                                {exitedDisplay.reason ? ` · ${exitedDisplay.reason}` : ''}.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit Date Passed Warning Banner */}
            {exitDateStatus.passed && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">
                                    Exit Date Has Passed - Action Required
                                </h3>
                                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                    {employee?.first_name}'s last working day was <strong>{exitDateStatus.exitDate?.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</strong> ({exitDateStatus.daysPassed} day{exitDateStatus.daysPassed !== 1 ? 's' : ''} ago).
                                    The exit process has not been completed.
                                </p>
                                <ul className="text-sm text-red-700 dark:text-red-300 mt-2 ml-4 list-disc">
                                    <li>Employee login is still enabled</li>
                                    <li>Status is still "Notice Period"</li>
                                    <li>Complete the exit to disable access and mark as "Exited"</li>
                                </ul>
                            </div>
                        </div>
                        <button
                            onClick={handleCompleteExit}
                            disabled={completingExit}
                            className="flex-shrink-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {completingExit ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <LogOut className="w-4 h-4" />
                                    Complete Exit
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            <EmployeeProfileView
                profile={employee}
                employees={employees}
                isEditable={true}
                editMode={isEditing}
                editedProfile={editFormData}
                onEditChange={handleEditChange}
                onPhotoUpload={handlePhotoUpload}
                isOwnProfile={false}
                currentUser={user}
            />

            {/* Action Panels */}
            {activeSidePanel && (
                <EmployeeActionPanels
                    employee={employee}
                    activeSidePanel={activeSidePanel}
                    onClose={() => setActiveSidePanel(null)}
                />
            )}
        </div>
    );
};

export default EmployeeDetails;
